import {t} from "@lingui/core/macro";

import {BuildQualifiedContentDescriptor} from "@khan/assignments-utils";
import type {
    CurationNode,
    FilteredContentDescriptors,
    TopicChild,
    TopicMap,
    TopicMapEntry,
} from "@khan/class-types";

// For the given parent topic, get all of the descendants that are content items
// (e.g. Video, Article).  Ignore descendants that are topics (i.e. TopicItems).
export function GetAllContentItems(
    topicMap: TopicMap,
    parentId: string,
): TopicChild[] {
    const parent: TopicMapEntry | null | undefined = topicMap[parentId];
    const content: TopicChild[] = [];

    if (!parent) {
        return [];
    }

    (parent.children || []).forEach((topicChild) => {
        if (topicChild.kind === "Topic") {
            const newParent: TopicMapEntry = topicMap[topicChild.id];
            if (newParent) {
                const newChildren: readonly TopicChild[] | null | undefined =
                    newParent.children;
                if (newChildren) {
                    const childContent: TopicChild[] = GetAllContentItems(
                        topicMap,
                        topicChild.id,
                    );
                    content.push(...childContent);
                }
            }
        } else {
            content.push(topicChild);
        }
    });
    return content;
}

type ContentKinds = [
    "Exercise",
    "Video",
    "Article",
    "Scratchpad",
    "Challenge",
    "Interactive",
    "Project",
    "Talkthrough",
    "TopicQuiz",
    "Quiz",
    "TopicUnitTest",
    "UnitTest",
    "CourseChallenge",
    "Topic",
    "AIGuideActivity",
];
export type ContentKind = ContentKinds[number];

export const GetContentKindLabel = (kind: ContentKind): string => {
    return (
        (
            {
                Exercise: t`Exercise`,
                Video: t`Video`,
                Article: t`Article`,
                Scratchpad: t`Scratchpad`,
                Challenge: t`Challenge`,
                Interactive: t`Interactive`,
                Project: t`Project`,
                Talkthrough: t`Talkthrough`,
                TopicQuiz: t`Quiz`,
                Quiz: t`Quiz`,
                TopicUnitTest: t`Unit Test`,
                UnitTest: t`Unit Test`,
                CourseChallenge: t`Course Challenge`,
                Topic: t`Topic`,
                AIGuideActivity: t`AI Activity`,
            } satisfies Record<ContentKind, string>
        )[kind] || kind
    );
};

// Get the qualified content descriptor for the given topic child.
// The process is different for a CourseChallenge vs. any other type of content.
export function GetQualifiedContentDescriptor(
    child: TopicChild,
    parent: TopicMapEntry,
    domain: CurationNode,
): string | null | undefined {
    const kind = child.kind;

    // If the child is a TopicItem (i.e. not content), return null.
    if (kind === "Topic") {
        return null;
    }
    const contentId = child.contentId;
    const contentDescriptor = `${kind}:${contentId}`;

    // CourseChallenges have the same contentID as their corresponding
    // Course, but they do not have a full topic path like other
    // types of learnable content. Since a Course Challenge has the same
    // contentID as its course, we should not use corresponding
    // topic data from the topicMap.
    const qualifiedDescriptor =
        kind === "CourseChallenge"
            ? BuildQualifiedContentDescriptor(
                  domain.id,
                  contentId,
                  null,
                  null,
                  contentDescriptor,
              )
            : BuildQualifiedContentDescriptor(
                  parent.domainId,
                  parent.courseId,
                  parent.unitId,
                  parent.lessonId,
                  contentDescriptor,
              );

    return qualifiedDescriptor;
}

// Get all of the topicChildren that pass the current filter state.
// If there is no active filter, filteredChildren will include all
// topicChildren.
export function GetFilteredTopicChildren(
    topicChildren: readonly TopicChild[] | null | undefined,
    topicMap: TopicMap,
    filteredContentDescriptors: FilteredContentDescriptors,
    parentId: string,
    domain: CurationNode,
    filterIsActive: boolean,
): TopicChild[] {
    return (topicChildren || []).filter((child) => {
        if (filterIsActive) {
            if (child.kind === "Topic") {
                // Child is a TopicItem. Check its entry in the TopicMap to see if
                // it or any of it's descendants are in the filtered set.
                const entry: TopicMapEntry = topicMap[child.id];
                return (
                    entry &&
                    (entry.inFilteredSet || entry.descendantInFilteredSet)
                );
            }

            // Child is a content item.
            // Check FilteredContentDescriptors to see if it's in the filtered
            // set. Content items have no descendants so we don't need to worry
            // about those.
            const qualifiedDescriptor = GetQualifiedContentDescriptor(
                child,
                topicMap[parentId],
                domain,
            );

            // If filteredContentDescriptors is empty, that means no filter has
            // been initialized yet, so all content will be in the filtered set.
            return (
                Object.keys(filteredContentDescriptors).length === 0 ||
                (qualifiedDescriptor &&
                    filteredContentDescriptors[qualifiedDescriptor])
            );
        }

        // If there is no active filter, then all content will be in the
        // filtered set.
        return true;
    });
}

// A filter is active if either there is a keyword search value or there is at
// least one selected content type.
export function IsFilterActive(
    searchValue: string,
    selectedContentTypes: readonly string[],
): boolean {
    return searchValue !== "" || selectedContentTypes.length !== 0;
}
